import React, { ChangeEvent, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import {
  UilDownloadAlt,
  UilFileAlt,
  UilUpload,
  UilCheckCircle
} from '@iconscout/react-unicons';

import { toast } from 'react-toastify';

import { importFoodExcel, ImportResult } from 'services/foodImportService';

const FoodImport: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [isUploading, setIsUploading] = useState(false);

  const [isFileSelectorHovered, setIsFileSelectorHovered] = useState(false);

  const [result, setResult] = useState<ImportResult | null>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    setResult(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const allowedExtensions = ['.xlsx', '.xls'];

    const fileName = file.name.toLowerCase();

    const isValidFile = allowedExtensions.some(extension =>
      fileName.endsWith(extension)
    );

    if (!isValidFile) {
      toast.error('Please select an Excel file (.xlsx or .xls).');

      event.target.value = '';
      setSelectedFile(null);

      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an Excel file first.');

      return;
    }

    setIsUploading(true);
    setResult(null);

    try {
      const importResult = await importFoodExcel(selectedFile);

      setResult(importResult);

      toast.success(
        importResult.message || 'Food Excel imported successfully.'
      );

      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error(error);

      toast.error(error?.message || 'Failed to import food Excel file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');

    link.href = '/files/MLAS_Food_Import_Template.xlsx';

    link.download = 'MLAS_Food_Import_Template.xlsx';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  return (
    <div className="mb-9">
      <Row className="g-3">
        {/* Page Header */}
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">Import Foods</h3>

              <p className="text-body-secondary mb-0">
                Import food entries using an excel file.
              </p>
            </div>

            {/* <Button variant="outline-primary" onClick={handleDownloadTemplate}>
              <UilDownloadAlt size={18} className="me-1" />
              Download Excel Template
            </Button> */}
          </div>
        </Col>

        {/* Upload Card */}
        <Col xs={12}>
          <Card>
            <Card.Body>
              <Alert
                variant="info"
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>Supported files:</strong> .xlsx and .xls
                  <br />
                  <span className="small">
                    The Excel file must follow the required food import format.
                  </span>
                </div>

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleDownloadTemplate}
                >
                  <UilDownloadAlt size={16} className="me-1" />
                  Download Excel Template
                </Button>
              </Alert>

              <Form.Group className="mb-4">
                <Form.Label>Select Excel File</Form.Label>

                <div
                  className="border rounded p-4 text-center"
                  style={{
                    borderStyle: 'dashed',
                    cursor: 'pointer'
                  }}
                  onClick={handleChooseFile}
                  onMouseEnter={() => setIsFileSelectorHovered(true)}
                  onMouseLeave={() => setIsFileSelectorHovered(false)}
                >
                  <UilUpload
                    size={40}
                    className="mb-2"
                    style={{
                      color: isFileSelectorHovered ? '#0d6efd' : undefined
                    }}
                  />

                  {selectedFile ? (
                    <>
                      <h6 className="mb-1">{selectedFile.name}</h6>

                      <p className="text-body-secondary mb-0">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <h6
                        className="mb-1"
                        style={{
                          color: isFileSelectorHovered ? '#0d6efd' : undefined
                        }}
                      >
                        Click to select Excel file
                      </h6>

                      <p className="text-body-secondary mb-0">.xlsx or .xls</p>
                    </>
                  )}
                </div>

                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="d-none"
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <UilUpload size={18} className="me-1" />
                      Import Food
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Import Result */}
        {result && (
          <Col xs={12}>
            <Card>
              <Card.Body>
                <div className="d-flex align-items-center mb-4">
                  <UilCheckCircle size={30} className="text-success me-2" />

                  <div>
                    <h5 className="mb-0">Import Completed</h5>

                    {result.status && (
                      <span className="text-body-secondary">
                        Status: {result.status}
                      </span>
                    )}
                  </div>
                </div>

                <Row className="g-3 mb-4">
                  <Col md={3}>
                    <div className="border rounded p-3">
                      <div className="text-body-secondary small">
                        Total Rows
                      </div>

                      <h4 className="mb-0">{result.totalRows ?? 0}</h4>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <div className="text-body-secondary small">
                        Successful
                      </div>

                      <h4 className="mb-0 text-success">
                        {result.successfulRows ?? 0}
                      </h4>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <div className="text-body-secondary small">Failed</div>

                      <h4 className="mb-0 text-danger">
                        {result.failedRows ?? 0}
                      </h4>
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="border rounded p-3">
                      <div className="text-body-secondary small">Skipped</div>

                      <h4 className="mb-0">{result.skippedRows ?? 0}</h4>
                    </div>
                  </Col>
                </Row>

                {/* Import Errors */}
                {result.errors && result.errors.length > 0 && (
                  <>
                    <h5 className="mb-3">Import Errors</h5>

                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Row</th>

                            <th>Error Type</th>

                            <th>Error Message</th>
                          </tr>
                        </thead>

                        <tbody>
                          {result.errors.map((error, index) => (
                            <tr key={index}>
                              <td>{error.rowNumber ?? '-'}</td>

                              <td>{error.errorType ?? '-'}</td>

                              <td>{error.errorMessage ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}

                {(!result.errors || result.errors.length === 0) && (
                  <Alert variant="success" className="mb-0">
                    <UilFileAlt size={18} className="me-1" />
                    No row-level import errors were reported.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default FoodImport;
